import { ChatAIStreamRequest } from '@/interfaces';
import { fetchWithAuth } from './fetchClientConfig';

type StreamCallbacks = {
	onStart?: (data: any) => void;
	onContent?: (text: string) => void;
	onDone?: (finalData: any) => void;
	onError?: (err: Error) => void;
};

export class ChatAIStream {
	constructor(private baseUrl: string) {}

	async streamChatMessage(
		body: ChatAIStreamRequest,
		callbacks: StreamCallbacks
	): Promise<void> {
		try {
			const response = await fetchWithAuth(`${this.baseUrl}/chatbot/stream`, {
				method: 'POST',
				body: JSON.stringify(body),
			});

			if (!response.ok || !response.body) {
				throw new Error(`HTTP error: ${response.status}`);
			}

			const reader = response.body.getReader();
			const decoder = new TextDecoder();
			let accumulated = '';

			let isReading = true;
			while (isReading) {
				const { done, value } = await reader.read();
				if (done) {
					isReading = false;
					break;
				}

				const chunk = decoder.decode(value);
				const lines = chunk.split('\n');

				for (const line of lines) {
					if (!line.startsWith('data: ')) continue;
					const json = JSON.parse(line.slice(6));

					switch (json.type) {
						case 'start':
							callbacks.onStart?.(json);
							break;

						case 'content':
							if (json.content) {
								accumulated += json.content;
								callbacks.onContent?.(accumulated);
							}
							break;

						case 'done':
							callbacks.onDone?.({ ...json, fullMessage: accumulated });
							break;

						default:
							// fallback (e.g. { finished: true })
							if (json.finished || json.content) {
								if (json.content) {
									accumulated += json.content;
									callbacks.onContent?.(accumulated);
								}
								if (json.finished) {
									callbacks.onDone?.({ ...json, fullMessage: accumulated });
								}
							}
							break;
					}
				}
			}
		} catch (err: any) {
			console.error('Streaming error:', err);
			callbacks.onError?.(err);
		}
	}
}

// Export instance
export const chatAIStreamService = new ChatAIStream(
	process.env.NEXT_PUBLIC_AI_API!
);
